import { isWorkflowFormVersionAvailable } from '@app/shared/workflow-commands/workflow-form-layout';
import { isUnsupportedCaptureForm } from './workflow-capture-form-validation';
import {
  isHearingPanelPopulation,
  isCombinedVotingRound,
} from '@app/shared/workflow-voting/voting-sources';
import {
  completionProviders,
  type WorkflowCompletionRequirement,
} from '@app/shared/workflow-commands/workflow-completion-requirements';
import type {
  WorkflowCapabilityReference,
  WorkflowDefinitionDocument,
  WorkflowDefinitionEdge,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';
import { routeDestinationPresentation } from './workflow-route-presentation';
import {
  noticePolicyDescription,
  noticeRequirementIssue,
} from '@app/phms/workflow-blocks/notice-policy';
import { projectWorkflowDraftGraph } from './workflow-draft-graph-projection';
import type { StepChecklistTarget } from './workflow-step-checklist';
import { isVotingRequirement } from '@app/shared/workflow-voting/voting.models';

export type WorkflowOverviewTarget =
  | StepChecklistTarget
  | 'requirements'
  | 'decisions';
export type WorkflowOverviewStatus =
  | 'configured'
  | 'attention'
  | 'inspect'
  | 'optional';

export interface WorkflowOverviewAttention {
  key: string;
  nodeId: string | null;
  label: string;
  detail: string;
  target: WorkflowOverviewTarget;
  requirementId?: string;
  edgeId?: string;
}

export interface WorkflowOverviewRequirement {
  key: string;
  label: string;
  typeLabel: string;
  detail: string;
  status: WorkflowOverviewStatus;
  /** A retained or unfamiliar contract is information until a defect is known. */
  requiresCorrection?: boolean;
}

export interface WorkflowOverviewPath {
  key: string;
  label: string;
  destinationLabel: string;
  detail: string;
  movement: 'forward' | 'return' | 'unknown';
  edgeIds: string[];
  requiresInspection: boolean;
}

export interface WorkflowOverviewStep {
  key: string;
  sourceIndex: number;
  nodeId: string | null;
  label: string;
  identityState: 'unique' | 'missing' | 'ambiguous' | 'invalid';
  responsibility: {
    label: string;
    detail: string;
    status: WorkflowOverviewStatus;
  };
  requirements: WorkflowOverviewRequirement[];
  completionDetail: string;
  paths: WorkflowOverviewPath[];
  attention: WorkflowOverviewAttention[];
}

export interface WorkflowOverviewPresentation {
  steps: WorkflowOverviewStep[];
  attention: WorkflowOverviewAttention[];
  workflowAttention: WorkflowOverviewAttention[];
  otherNodeCount: number;
  scopeNote: string;
}

const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';
const rows = <T>(value: T[] | null | undefined): T[] =>
  Array.isArray(value) ? value : [];
const name = (node: WorkflowDefinitionNode): string =>
  text(node.displayName) ||
  text(node.stepCode) ||
  text(node.id) ||
  'Unnamed step';
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const exactReference = (
  left: WorkflowCapabilityReference | null | undefined,
  right: WorkflowCapabilityReference | null | undefined,
): boolean =>
  !!left?.key &&
  !!right?.key &&
  !!text(left.key.provider) &&
  !!text(left.key.kind) &&
  !!text(left.key.code) &&
  Number.isSafeInteger(left.key.version) &&
  left.key.version > 0 &&
  !!text(left.contractHash) &&
  !!text(left.inputSchemaHash) &&
  !!text(left.outputSchemaHash) &&
  left.key.provider === right.key.provider &&
  left.key.kind === right.key.kind &&
  left.key.code === right.key.code &&
  left.key.version === right.key.version &&
  left.contractHash === right.contractHash &&
  left.inputSchemaHash === right.inputSchemaHash &&
  left.outputSchemaHash === right.outputSchemaHash;

/** A read-only inventory of local working settings, never validation or execution order. */
export function workflowOverviewPresentation(
  document: WorkflowDefinitionDocument | null,
): WorkflowOverviewPresentation {
  const nodes = rows(document?.nodes);
  const edges = rows(document?.edges);
  const attention: WorkflowOverviewAttention[] = [];
  const graph = projectWorkflowDraftGraph(document);
  const safeDocument = {
    ...document,
    nodes: nodes
      .filter((node) => record(node))
      .map((node) => ({
        ...node,
        displayName:
          typeof node.displayName === 'string' ? node.displayName : null,
        stepCode: typeof node.stepCode === 'string' ? node.stepCode : null,
      })),
    edges: edges.filter((edge) => record(edge)),
  } as WorkflowDefinitionDocument;
  const steps = nodes
    .map((node, sourceIndex) => ({ node, sourceIndex }))
    .filter(({ node }) => node?.typeCode === 'human_step')
    .sort((left, right) => {
      const order = (node: WorkflowDefinitionNode) =>
        Number.isFinite(node.displayOrder)
          ? node.displayOrder
          : Number.POSITIVE_INFINITY;
      return (
        order(left.node) - order(right.node) ||
        (typeof left.node.id === 'string' ? left.node.id : '').localeCompare(
          typeof right.node.id === 'string' ? right.node.id : '',
        ) ||
        left.sourceIndex - right.sourceIndex
      );
    })
    .map(({ node, sourceIndex }): WorkflowOverviewStep => {
      const id = text(node.id);
      const sourceUnique =
        !!id &&
        nodes.filter((candidate) => candidate?.id === node.id).length === 1;
      const projected = graph.nodes.find(
        (candidate) =>
          candidate.sourceIndex === sourceIndex &&
          candidate.sourceId === node.id,
      );
      const identityState = !id
        ? 'missing'
        : !sourceUnique
          ? 'ambiguous'
          : !projected || projected.isInvalid
            ? 'invalid'
            : 'unique';
      const nodeId = identityState === 'unique' ? node.id : null;
      const key = `step-${sourceIndex}`;
      const stepAttention: WorkflowOverviewAttention[] = [];
      const addAttention = (
        suffix: string,
        label: string,
        detail: string,
        target: WorkflowOverviewTarget,
        identity: { requirementId?: string; edgeId?: string } = {},
      ) =>
        stepAttention.push({
          key: `${key}-${suffix}`,
          nodeId,
          label,
          detail,
          target,
          ...identity,
        });
      if (!nodeId)
        addAttention(
          'identity',
          identityState === 'missing'
            ? 'Missing step identity'
            : identityState === 'ambiguous'
              ? 'Ambiguous step identity'
              : 'Retained step needs inspection',
          'Inspect the retained step on the map; a specific editor cannot be chosen safely.',
          'paths',
        );

      const responsibility = stepResponsibility(safeDocument, node, nodeId);
      if (
        responsibility.status === 'attention' ||
        responsibility.status === 'inspect'
      )
        addAttention(
          'responsibility',
          responsibility.label,
          responsibility.detail,
          'responsibility',
        );
      const requirementRows = rows(
        node.declaration?.behavior?.completionRequirements,
      );
      const malformedRequirements =
        node.declaration?.behavior?.completionRequirements != null &&
        !Array.isArray(node.declaration.behavior.completionRequirements);
      if (malformedRequirements)
        addAttention(
          'requirements-collection',
          'Completion requirements need inspection',
          'The saved completion requirement collection is malformed. Its entries cannot be counted or interpreted here.',
          'requirements',
        );
      const requirements = requirementRows.map((requirement, index) =>
        requirementPresentation(
          safeDocument,
          node,
          requirement,
          index,
          requirementRows,
        ),
      );
      for (const requirement of requirements)
        if (
          requirement.status !== 'configured' &&
          requirement.requiresCorrection !== false
        )
          addAttention(
            requirement.key,
            requirement.label,
            requirement.detail,
            'requirements',
            {
              requirementId: (() => {
                const saved =
                  requirementRows[requirements.indexOf(requirement)];
                return text(saved?.id) &&
                  requirementRows.filter((row) => row?.id === saved.id)
                    .length === 1
                  ? saved.id
                  : undefined;
              })(),
            },
          );
      const commands = rows(node.declaration?.behavior?.commands);
      const complete = commands.filter(
        (command) =>
          command?.capability?.key?.provider === 'workflow' &&
          command.capability.key.kind === 'command' &&
          command.capability.key.code === 'workflow.step.complete' &&
          command.capability.key.version === 1 &&
          rows(document?.capabilityReferences).filter((reference) =>
            exactReference(reference, command.capability),
          ).length === 1,
      );
      const typed =
        [
          '1.8',
          '1.9',
          '1.10',
          '1.11',
          '1.12',
          '1.13',
          '1.14',
          '1.15',
          '1.16',
          '1.17',
          '1.18', '1.19', '1.20', '1.21', '1.22',
        ].includes(document?.payloadSchemaVersion ?? '') &&
        complete.length === 1;
      const knownAdvancingAction = commands.some((command) => {
        const key = command?.capability?.key;
        return (
          key?.provider === 'workflow' &&
          key.kind === 'command' &&
          key.version === 1 &&
          ['workflow.form.submit', 'workflow.review.submit'].includes(key.code)
        );
      });
      const knownSaveOnlyEvidence = commands.every((command) => {
        const key = command?.capability?.key;
        return (
          key?.provider === 'workflow' &&
          key.kind === 'command' &&
          key.version === 1 &&
          [
            'workflow.form.save',
            'workflow.review.request',
            'workflow.review.record',
            'workflow.step.complete',
          ].includes(key.code)
        );
      });
      const malformedActions =
        node.declaration?.behavior?.commands != null &&
        !Array.isArray(node.declaration.behavior.commands);
      const completionDetail =
        malformedRequirements || malformedActions
          ? 'Saved completion settings need inspection. Their behavior cannot be interpreted from this overview.'
          : requirements.length
            ? typed && !knownAdvancingAction
              ? knownSaveOnlyEvidence
                ? 'Meet every completion requirement for the current visit, then choose Complete step. Saving evidence keeps the step open.'
                : 'Meet every completion requirement for the current visit, then choose Complete step. Installed actions may also control completion.'
              : 'Saved completion requirements are listed here. Inspect the actions that control step completion.'
            : typed
              ? 'No typed completion requirements are saved. Complete step and any installed action checks still need review.'
              : commands.length
                ? 'Completion is controlled by the attached actions and module checks; no typed completion requirements are saved.'
                : 'No step actions or typed completion requirements are saved. Inspect actions for any module completion behavior.';
      const unresolvedActions =
        malformedActions ||
        commands.some(
          (command) =>
            !exactReference(command?.capability, command?.capability) ||
            rows(document?.capabilityReferences).filter((reference) =>
              exactReference(reference, command?.capability),
            ).length !== 1 ||
            commands.filter((other) => {
              const left = command?.capability?.key,
                right = other?.capability?.key;
              return (
                left &&
                right &&
                left.provider === right.provider &&
                left.kind === right.kind &&
                left.code === right.code &&
                left.version === right.version
              );
            }).length !== 1,
        );
      if (
        unresolvedActions ||
        (requirements.length > 0 &&
          (complete.length !== 1 || knownAdvancingAction))
      )
        addAttention(
          'completion',
          'Inspect completion actions',
          knownAdvancingAction && requirements.length > 0
            ? 'A saved Submit form or Submit review action advances this step alongside typed completion requirements. Inspect its completion behavior and Complete step action.'
            : 'A saved action contract is missing or ambiguous, or the typed requirements are not linked to one exact Complete step action. Inspect the saved actions and module checks.',
          'actions',
        );
      const paths = sourceUnique ? stepPaths(safeDocument, node) : [];
      if (!paths.length)
        addAttention(
          'paths',
          'No outgoing path is available',
          nodeId
            ? 'Choose what happens after this step completes.'
            : 'Resolve the step identity before inspecting its outgoing paths.',
          'paths',
        );
      else if (paths.some((path) => path.requiresInspection))
        addAttention(
          'paths',
          'Inspect workflow paths',
          'Some saved paths need inspection; their conditions or identities cannot be summarized as a single next step.',
          'paths',
          {
            edgeId: (() => {
              const affected = paths.filter((path) => path.requiresInspection);
              const id =
                affected.length === 1 && affected[0].edgeIds.length === 1
                  ? affected[0].edgeIds[0]
                  : undefined;
              return id && edges.filter((edge) => edge?.id === id).length === 1
                ? id
                : undefined;
            })(),
          },
        );
      const step: WorkflowOverviewStep = {
        key,
        sourceIndex,
        nodeId,
        label: name(node),
        identityState,
        responsibility,
        requirements,
        completionDetail,
        paths,
        attention: stepAttention,
      };
      attention.push(...stepAttention);
      return step;
    });
  const workflowAttention: WorkflowOverviewAttention[] = [];
  const unknownNodes = nodes.filter(
    (node) =>
      !node ||
      ![
        'entry',
        'human_step',
        'automated_step',
        'routing_decision',
        'terminal',
      ].includes(node.typeCode),
  );
  if (unknownNodes.length)
    workflowAttention.push({
      key: 'unknown-nodes',
      nodeId: null,
      label: `${unknownNodes.length} saved node${unknownNodes.length === 1 ? '' : 's'} need inspection`,
      detail:
        'Unrecognized or malformed nodes remain in the workflow. Inspect workflow paths to see their exact saved configuration.',
      target: 'paths',
    });
  const orphanEdges = edges.filter(
    (edge) =>
      !edge ||
      !text(edge.fromNodeId) ||
      nodes.filter((node) => node?.id === edge.fromNodeId).length !== 1,
  );
  if (orphanEdges.length)
    workflowAttention.push({
      key: 'unresolved-path-sources',
      nodeId: null,
      label: `${orphanEdges.length} path source${orphanEdges.length === 1 ? '' : 's'} need inspection`,
      detail:
        'Some saved paths have missing or ambiguous sources and cannot be attached to one step overview.',
      target: 'paths',
    });
  attention.push(...workflowAttention);
  return {
    steps,
    attention,
    workflowAttention,
    otherNodeCount: nodes.length - steps.length,
    scopeNote:
      'This overview describes local working settings. Step order is for display; the connections below are saved paths. Save and validate in Review to check the complete workflow and installed contracts.',
  };
}

function stepResponsibility(
  document: WorkflowDefinitionDocument,
  node: WorkflowDefinitionNode,
  nodeId: string | null,
): WorkflowOverviewStep['responsibility'] {
  const policies = rows(document.assignmentPolicies);
  const attached = policies.filter((policy) => policy?.nodeId === node.id);
  const referenceId = node.declaration?.behavior?.assignmentPolicyId;
  if (!attached.length && !text(referenceId))
    return {
      label:
        !document.subjectTypeCode &&
        ['1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(
          document.payloadSchemaVersion,
        )
          ? 'Choose responsible roles'
          : 'Existing assignment behavior',
      detail:
        !document.subjectTypeCode &&
        ['1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(
          document.payloadSchemaVersion,
        )
          ? 'Choose eligible roles in Work before publishing.'
          : 'No step responsibility policy is attached. Workflow policies do not provide step responsibility.',
      status:
        !document.subjectTypeCode &&
        ['1.13', '1.14', '1.15', '1.16', '1.17', '1.18', '1.19', '1.20', '1.21', '1.22'].includes(
          document.payloadSchemaVersion,
        )
          ? 'attention'
          : 'optional',
    };
  const policy = attached.length === 1 ? attached[0] : null;
  const modes: Record<string, string> = {
    queue_only: 'Queue only',
    claim: 'Claim based',
    direct: 'Direct assignment',
    claim_or_direct: 'Claim or direct assignment',
  };
  if (
    !nodeId ||
    !policy ||
    policy.scopeCode !== 'step' ||
    !text(policy.id) ||
    policies.filter((candidate) => candidate?.id === policy.id).length !== 1 ||
    (node.declaration != null && referenceId !== policy.id)
  )
    return {
      label: 'Responsibility needs inspection',
      detail:
        'The attached step policy or its page reference is missing, ambiguous or not linked. Its saved settings are retained.',
      status: 'attention',
    };
  const roleAssignment = policy.assignmentBasisCode === 'roles';
  if (
    (roleAssignment
      ? !!text(policy.initialQueueCode) ||
        rows(policy.eligibleQueueCodes).length > 0 ||
        policy.assignmentModeCode === 'queue_only'
      : !text(policy.initialQueueCode) ||
        !rows(policy.eligibleQueueCodes).includes(policy.initialQueueCode)) ||
    !Object.hasOwn(modes, policy.assignmentModeCode) ||
    (policy.assignmentModeCode !== 'queue_only' &&
      !rows(policy.eligibleRoleCodes).some((role) => text(role)))
  )
    return {
      label: 'Responsibility needs attention',
      detail: roleAssignment
        ? 'Choose eligible roles in Work.'
        : 'Inspect the retained queue assignment settings or switch to role assignment in Work.',
      status: 'attention',
    };
  return {
    label: `${
      roleAssignment
        ? rows(policy.eligibleRoleCodes)
            .map((role) => text(role).replace(/[_-]+/g, ' '))
            .join(', ')
        : policy.initialQueueCode
    } · ${modes[policy.assignmentModeCode]}`,
    detail:
      'Step responsibility is recorded. Live eligibility and permissions are checked when work is assigned.',
    status: 'configured',
  };
}

function requirementPresentation(
  document: WorkflowDefinitionDocument,
  node: WorkflowDefinitionNode,
  requirement: WorkflowCompletionRequirement,
  index: number,
  requirements: WorkflowCompletionRequirement[],
): WorkflowOverviewRequirement {
  const key = `requirement-${index}`;
  const provider = text(requirement?.provider);
  const typeLabel =
    provider === completionProviders.form
      ? 'Form'
      : provider === completionProviders.document
        ? 'Document'
        : provider === completionProviders.decision
          ? 'Decision'
          : 'Saved requirement';
  const retained = (
    label: string,
    detail: string,
    requiresCorrection = true,
  ): WorkflowOverviewRequirement => ({
    key,
    label,
    typeLabel,
    detail,
    status: 'inspect',
    requiresCorrection,
  });
  if (
    !requirement ||
    !text(requirement.id) ||
    requirements.filter((candidate) => candidate?.id === requirement.id)
      .length !== 1
  )
    return retained(
      provider || 'Unavailable saved requirement',
      'This saved requirement has a missing or ambiguous identity. Its configuration is retained for inspection.',
    );
  if (
    !provider ||
    !Number.isSafeInteger(requirement.version) ||
    requirement.version < 1 ||
    !Number.isSafeInteger(requirement.settingsVersion) ||
    requirement.settingsVersion < 1 ||
    !record(requirement.settings)
  )
    return retained(
      provider || 'Unavailable saved requirement',
      'The saved requirement provider or settings are incomplete or malformed.',
    );
  const blockOwned =
    provider === completionProviders.sdmf &&
    (requirement.version === 1 || requirement.version === 2) &&
    requirement.settingsVersion === requirement.version
      ? { label: 'Complete SDMF', typeLabel: 'SDMF' }
      : provider === completionProviders.hearingGrantReview &&
          requirement.version === 2 &&
          requirement.settingsVersion === 1
        ? { label: 'Legal review decision recorded', typeLabel: 'Legal review' }
        : requirement.version === 1 && requirement.settingsVersion === 1
          ? provider === completionProviders.schedulingRequested
            ? { label: 'Scheduling requested', typeLabel: 'Hearing scheduling' }
            : provider === completionProviders.hearingScheduled
              ? { label: 'Hearing scheduled', typeLabel: 'Hearing scheduling' }
              : [
                    completionProviders.hearingDecision,
                    completionProviders.hearingConduct,
                    completionProviders.hearingGrantReview,
                  ].includes(
                    provider as typeof completionProviders.hearingDecision,
                  )
                ? {
                    label:
                      provider === completionProviders.hearingGrantReview
                        ? 'Grant review approved'
                        : provider === completionProviders.hearingConduct
                          ? 'Hearing outcome recorded'
                          : 'Hearing decision recorded',
                    typeLabel: 'Hearing decision',
                  }
                : null
          : null;
  if (blockOwned)
    return {
      key,
      ...blockOwned,
      status: 'inspect',
      requiresCorrection: false,
      detail:
        'The installed domain provider checks this requirement against the linked hearing and its page block. Open Work to review its settings; save and validate to check compatibility.',
    };
  if (
    !Object.values(completionProviders).includes(
      provider as (typeof completionProviders)[keyof typeof completionProviders],
    ) ||
    (requirement.version !== 1 &&
      !isVotingRequirement(provider, requirement.version)) ||
    requirement.settingsVersion !== 1
  )
    return retained(
      provider || 'Unavailable saved requirement',
      'This saved provider, version or settings format needs inspection. Installed compatibility is not established by this overview.',
      false,
    );
  const settings = requirement.settings;
  if (
    isVotingRequirement(provider, requirement.version) ||
    provider === completionProviders.quorum
  ) {
    return {
      key,
      label:
        provider === completionProviders.votingPopulation
          ? node.declaration?.detail.blocks.some(isHearingPanelPopulation)
            ? 'Panel confirmed'
            : 'Voting population confirmed'
          : provider === completionProviders.votingRound
            ? 'Voting round completed'
            : node.declaration?.detail.blocks.some(isCombinedVotingRound)
              ? 'Vote and quorum result recorded'
              : 'Quorum evaluated',
      typeLabel: provider === completionProviders.quorum ? 'Quorum' : 'Voting',
      detail:
        'The installed voting provider interprets these saved checks. Save and validate to check their configuration.',
      status: 'inspect',
      requiresCorrection: false,
    };
  }
  if (provider === completionProviders.notices) {
    const issue = noticeRequirementIssue(settings);
    const producers = rows(node.declaration?.detail?.blocks).filter(
      (block) =>
        block.code === 'notices.delivery' &&
        block.provider === 'notices' &&
        [1, 2].includes(block.version) &&
        block.settings?.['noticeGroupId'] === settings['noticeGroupId'] &&
        block.settings?.['policyJson'] === settings['policyJson'],
    );
    return issue || producers.length !== 1
      ? retained(
          'Complete notice work',
          issue || 'The matching notice delivery group is unavailable.',
        )
      : {
          key,
          label: 'Complete notice work',
          typeLabel: 'Hearing notices',
          status: 'configured',
          detail: noticePolicyDescription(settings['policyJson']),
        };
  }
  const commands = rows(node.declaration?.behavior?.commands);
  const allowedSettings =
    provider === completionProviders.form
      ? ['formId']
      : provider === completionProviders.document
        ? [
            'documentTypeCode',
            'documentTypeLabel',
            'sourceMode',
            'allowedFormats',
          ]
        : ['sourceCommand', 'decisionKind', 'outcomeCodes'];
  if (
    Object.keys(settings).some((setting) => !allowedSettings.includes(setting))
  )
    return retained(
      provider,
      'Additional saved settings are retained. Inspect this requirement before interpreting its behavior.',
      false,
    );
  if (provider === completionProviders.form) {
    const formId = text(settings['formId']);
    const forms = commands.filter(
      (command) => formId && command?.captureForm?.id === formId,
    );
    if (forms.length !== 1)
      return retained(
        formId ? `Form (${formId})` : 'Form completed',
        'The referenced form is missing or ambiguous. Inspect the saved form requirement.',
      );
    const form = forms[0].captureForm!;
    const fields = rows(form.fields);
    const required = fields.filter((field) => field?.required === true);
    const label = text(form.title) || `Form (${formId})`;
    const source = forms[0].capability;
    if (
      source?.key?.provider !== 'workflow' ||
      source.key.kind !== 'command' ||
      source.key.code !== 'workflow.form.save' ||
      source.key.version !== 1 ||
      rows(document.capabilityReferences).filter((reference) =>
        exactReference(reference, source),
      ).length !== 1
    )
      return retained(
        label,
        'The form source is not one exact saved Save form action. Inspect its action and requirement.',
      );
    if (
      !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, form) ||
      isUnsupportedCaptureForm(form) ||
      !fields.length ||
      fields.some((field) => !field || !text(field.id) || !text(field.label)) ||
      new Set(fields.map((field) => field.id)).size !== fields.length
    )
      return retained(
        label,
        'The saved form definition needs inspection. Its fields are retained.',
      );
    return {
      key,
      label,
      typeLabel,
      status: 'configured',
      detail: `Save the form with all visible required answers${
        required.length
          ? ` (${required.length} required field${required.length === 1 ? '' : 's'}: ${required
              .slice(0, 4)
              .map((field) => field.label)
              .join(', ')}${required.length > 4 ? ', …' : ''})`
          : ''
      }.`,
    };
  }
  if (provider === completionProviders.document) {
    const label = text(settings['documentTypeLabel']) || 'Document supplied';
    const formats = Array.isArray(settings['allowedFormats'])
      ? settings['allowedFormats']
      : [];
    if (
      !text(settings['documentTypeCode']) ||
      !text(settings['documentTypeLabel']) ||
      settings['sourceMode'] !== 'new_visit' ||
      !formats.length ||
      formats.some((format) => !text(format)) ||
      new Set(formats).size !== formats.length
    )
      return retained(
        label,
        'The saved document name, formats or evidence source needs inspection.',
      );
    return {
      key,
      label,
      typeLabel,
      status: 'configured',
      detail: `Upload a new ${formats.map((format) => text(format).toUpperCase()).join(', ')} file for the current visit.`,
    };
  }
  const source = settings['sourceCommand'] as
    | WorkflowCapabilityReference
    | undefined;
  const sources = commands.filter((command) =>
    exactReference(command?.capability, source),
  );
  const outcomes = Array.isArray(settings['outcomeCodes'])
    ? settings['outcomeCodes']
    : [];
  const label =
    text(settings['decisionKind']) === 'review'
      ? 'Review recorded'
      : 'Decision recorded';
  if (
    sources.length !== 1 ||
    rows(document.capabilityReferences).filter((reference) =>
      exactReference(reference, source),
    ).length !== 1 ||
    !text(settings['decisionKind']) ||
    !outcomes.length ||
    outcomes.some((outcome) => !text(outcome)) ||
    new Set(outcomes).size !== outcomes.length
  )
    return retained(
      label,
      'The saved decision source or allowed results are missing or ambiguous. Inspect the decision requirement and its installed action.',
    );
  return {
    key,
    label,
    typeLabel,
    status: 'configured',
    detail: `Record ${outcomes.map((outcome) => text(outcome).replaceAll('_', ' ')).join(' or ')} for the current visit. The installed action defines who can record this decision.`,
  };
}

function stepPaths(
  document: WorkflowDefinitionDocument,
  source: WorkflowDefinitionNode,
): WorkflowOverviewPath[] {
  const graphExecution = [
    '1.3',
    '1.4',
    '1.5',
    '1.6',
    '1.7',
    '1.8',
    '1.9',
    '1.10',
    '1.11',
    '1.12',
    '1.13',
    '1.14',
    '1.15',
    '1.16',
    '1.17',
    '1.18', '1.19', '1.20', '1.21', '1.22',
  ].includes(document.payloadSchemaVersion);
  const outgoing = document.edges.filter(
    (edge) => edge?.fromNodeId === source.id,
  );
  const uniqueEdge = (edge: WorkflowDefinitionEdge) =>
    !!text(edge.id) &&
    document.edges.filter((candidate) => candidate?.id === edge.id).length ===
      1;
  const present = (
    edge: WorkflowDefinitionEdge,
    index: number,
    via?: {
      node: WorkflowDefinitionNode;
      edge: WorkflowDefinitionEdge;
      inspect: boolean;
      outcomeLabels?: Record<string, string>;
    },
  ): WorkflowOverviewPath => {
    const movement =
      edge.pathKindCode === 'return'
        ? 'return'
        : edge.pathKindCode == null
          ? 'forward'
          : 'unknown';
    const destination = routeDestinationPresentation(
      document,
      source.id,
      edge.toNodeId,
      movement === 'return' ? 'return' : 'forward',
    );
    const outcomes = rows(source.declaration?.behavior?.commands)
      .flatMap((command) => rows(command?.outcomes))
      .filter((outcome) => outcome?.code === edge.outcomeCode);
    const domainLabel = edge.outcomeCode
      ? via?.outcomeLabels?.[edge.outcomeCode]
      : undefined;
    const exactOutcome =
      !!domainLabel ||
      (outcomes.length === 1 &&
        outcomes[0].destinationNodeId === edge.toNodeId);
    const outcomeLabel = text(edge.outcomeCode)
      ? domainLabel ||
        (exactOutcome && text(outcomes[0]?.label)
          ? outcomes[0].label
          : `Outcome ${edge.outcomeCode}`)
      : via
        ? 'Unspecified outcome'
        : 'After completion';
    const requiresInspection =
      !graphExecution ||
      !uniqueEdge(edge) ||
      destination.requiresPathInspection ||
      destination.state === 'unselected' ||
      movement === 'unknown' ||
      !!via?.inspect ||
      (via
        ? !text(edge.outcomeCode) || !exactOutcome
        : edge.outcomeCode != null) ||
      outgoing.length > 1;
    const detail =
      destination.state === 'unselected'
        ? 'This saved path has no destination. Inspect workflow paths.'
        : movement === 'unknown'
          ? `Saved path kind “${String(edge.pathKindCode)}” needs inspection. Authored destination: ${destination.label}; movement cannot be inferred.`
          : !graphExecution
            ? `Recorded path connection to “${destination.label}”. Runtime movement must be checked against this workflow's execution format.`
            : `${via || outgoing.length > 1 || edge.outcomeCode != null ? 'If this path is selected: ' : ''}${destination.sentence}`;
    return {
      key: `path-${index}`,
      label: `${via ? `Via “${name(via.node)}” · ` : ''}${outcomeLabel}${movement === 'return' ? ' · Return visit' : ''}`,
      destinationLabel: destination.label,
      detail,
      movement,
      edgeIds: [via?.edge.id, edge.id].filter((id): id is string => !!text(id)),
      requiresInspection,
    };
  };
  if (
    outgoing.length === 1 &&
    outgoing[0].outcomeCode == null &&
    outgoing[0].pathKindCode == null
  ) {
    const edge = outgoing[0];
    const destinations = document.nodes.filter(
      (node) => node?.id === edge.toNodeId,
    );
    const router =
      destinations.length === 1 &&
      destinations[0].typeCode === 'routing_decision'
        ? destinations[0]
        : null;
    if (router) {
      const branches = document.edges.filter(
        (branch) => branch?.fromNodeId === router.id,
      );
      const incoming = document.edges.filter(
        (candidate) => candidate?.toNodeId === router.id,
      );
      if (branches.length) {
        const codes = branches.map((branch) => text(branch.outcomeCode));
        const declared = rows(source.declaration?.behavior?.commands).flatMap(
          (command) => rows(command?.outcomes),
        );
        const hearingOutcomes: Record<string, string> | undefined =
          router.routing?.conditionCode === 'hearing.outcome' &&
          router.routing.conditionVersion === 1 &&
          source.declaration?.behavior.completionRequirements?.some(
            (r) =>
              r.provider === completionProviders.hearingConduct &&
              r.version === 1,
          ) &&
          source.declaration.detail.blocks.some(
            (b) =>
              b.provider === 'hearing' &&
              b.code === 'hearing.decision' &&
              b.version === 2,
          )
            ? {
                grant: 'Grant',
                denial: 'Denial',
                no_decision: 'No decision',
                quorum_not_met: 'Quorum not met',
              }
            : router.routing?.conditionCode ===
                  'hearing.grant-review-outcome' &&
                router.routing.conditionVersion === 1 &&
                source.declaration?.behavior.completionRequirements?.some(
                  (r) =>
                    r.provider === completionProviders.hearingGrantReview &&
                    r.version === 2,
                ) &&
                source.declaration.detail.blocks.some(
                  (b) =>
                    b.provider === 'hearing' &&
                    b.code === 'hearing.grant-review' &&
                    b.version === 2,
                )
              ? { affirmed: 'Affirmed', not_affirmed: 'Not affirmed' }
              : undefined;
        const correspondence = hearingOutcomes
          ? branches.length === Object.keys(hearingOutcomes).length &&
            branches.every(
              (b) => !!b.outcomeCode && !!hearingOutcomes[b.outcomeCode],
            )
          : declared.length === branches.length &&
            branches.every((branch) => {
              const matches = declared.filter(
                (outcome) => outcome?.code === branch.outcomeCode,
              );
              return (
                matches.length === 1 &&
                matches[0].destinationNodeId === branch.toNodeId
              );
            });
        const inspect =
          !uniqueEdge(edge) ||
          incoming.length !== 1 ||
          (!hearingOutcomes &&
            (router.routing?.conditionCode !== 'review.result' ||
              router.routing.conditionVersion !== 1)) ||
          codes.some((code) => !code) ||
          new Set(codes).size !== codes.length ||
          !correspondence;
        return branches.map((branch, index) =>
          present(branch, index, {
            node: router,
            edge,
            inspect,
            outcomeLabels: hearingOutcomes,
          }),
        );
      }
    }
  }
  return outgoing.map((edge, index) => present(edge, index));
}
